/**
 * browser-loop — config + mode derivation.
 *
 * Reads three sources of truth, fail-safe defaults everywhere:
 *
 *   1. localStorage `hh_dev_loop_token` — minted via /admin/dev-loop
 *      page; presence + `dev_*` prefix → mode='dev_loop'.
 *   2. Build-time `NEXT_PUBLIC_BROWSER_LOOP_RUM=true` flag — turns on
 *      anonymous capture for real users (deferred until Phase 5+).
 *   3. Build-time `NEXT_PUBLIC_GIT_SHA` — stamped on every event so
 *      backend source-map resolve can pick the right .js.map.
 *
 * Borrowed from Council's env.ts pattern: a misconfigured env produces
 * `{ enabled: false }` plus ONE log line. Never throws — telemetry
 * crashing the host app is the worst failure mode (closed-loop §V.1
 * "bugs in the substrate are uniquely dangerous").
 */

const TOKEN_KEY = "hh_dev_loop_token";

export type BrowserLoopMode = "off" | "dev_loop" | "rum" | "agent_lab";

export interface BrowserLoopConfig {
  enabled: boolean;
  mode: BrowserLoopMode;
  bearer: string | null;
  /** Cross-origin ingestion endpoint. */
  ingestUrl: string;
  /** git_sha stamped on every event for source-map resolution. */
  gitSha: string | null;
  /** RUM sampling rate (0..1) — gated by build flag, project-tunable. */
  rumSampleRate: number;
  /** Hard cap on events kept in the ring buffer. */
  ringBufferCap: number;
  /** Batch POST interval in ms. */
  flushIntervalMs: number;
  /** Max events per POST batch (backend cap is 100). */
  maxBatchSize: number;
}

const DEFAULT_CONFIG: BrowserLoopConfig = {
  enabled: false,
  mode: "off",
  bearer: null,
  ingestUrl: "",
  gitSha: null,
  rumSampleRate: 0,
  ringBufferCap: 256,
  flushIntervalMs: 2000,
  maxBatchSize: 100,
};

/**
 * Pure function — no side effects, no DOM access except reading
 * localStorage. Safe to call from a React effect.
 */
export function readConfig(): BrowserLoopConfig {
  if (typeof window === "undefined") return DEFAULT_CONFIG;

  const apiBase = (process.env.NEXT_PUBLIC_API_URL || "").replace(/\/+$/, "");
  if (!apiBase) {
    // Without an API URL we have nowhere to ship. Silent off.
    return DEFAULT_CONFIG;
  }

  const ingestUrl = `${apiBase}/internal/v1/browser/events`;
  const gitSha = process.env.NEXT_PUBLIC_GIT_SHA || null;
  const rumEnabled = process.env.NEXT_PUBLIC_BROWSER_LOOP_RUM === "true";
  const rumSampleRate = Math.max(
    0,
    Math.min(1, Number(process.env.NEXT_PUBLIC_BROWSER_LOOP_RUM_SAMPLE) || 0.05),
  );

  let bearer: string | null = null;
  try {
    bearer = window.localStorage.getItem(TOKEN_KEY);
  } catch {
    // SecurityError when third-party cookies disabled / private mode quirks.
    // Fail-safe: no bearer means anonymous (rum) at most.
    bearer = null;
  }

  let mode: BrowserLoopMode = "off";
  if (bearer && bearer.startsWith("dev_")) {
    mode = "dev_loop";
  } else if (bearer && bearer.startsWith("internal_")) {
    mode = "agent_lab"; // injected by Playwright via page.addInitScript
  } else if (rumEnabled) {
    mode = "rum";
  }

  return {
    ...DEFAULT_CONFIG,
    enabled: mode !== "off",
    mode,
    bearer,
    ingestUrl,
    gitSha,
    rumSampleRate,
  };
}

/** Token storage — single source of truth used by activation page + reads. */
export function readToken(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function writeToken(token: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(TOKEN_KEY, token);
  } catch {
    // Same SecurityError class as read; activation page surfaces the failure.
  }
}

export function clearToken(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* silent */
  }
}
